/**
 * Floor-plan recognition: pictures of a plan go to the company's recognition service, which runs the app's own
 * pipeline (the neural detectors behind `/roboflow` and the C# assembly of `FloorplanRecognition`) and answers with a
 * project in the app's XML. The web only uploads, waits and opens the answer. The contract is
 * docs/tasks/floorplan-recognition/README.md; the numbers below are the app's.
 */

/** One plan per floor, at most as many floors as a project holds (`AddLevel.MaxLevels`). */
export const MAX_FLOORS = 5;
/** A picture larger than this is refused before it is read: a phone photo is 3–12 MB, a scan up to ~30. */
export const MAX_FILE_BYTES = 40 * 1024 * 1024;
/** The app shrinks a plan to this longest side before it is recognized (`AddBlueprintAnnotation.BlueprintTextureMaxSize`). */
export const PLAN_MAX_SIDE = 2048;
/** A result below this opens on the plan for the walls to be checked (`FpRecognitionSettings.HighQualityScore`). */
export const HIGH_QUALITY_SCORE = 90;
/** A result below this asks the user to check it (`FpRecognitionSettings.ResultDialogSuppressScore`). */
export const CHECK_SCORE = 97;
/**
 * The web's own ceiling on the wait. The service times a job out itself (the app waits 330 s for a plain run and
 * 615 s with the image-cleaning pass, `FpRecognitionSettings.RecognitionTimeoutSeconds`); this only covers a service
 * that stopped answering about a job.
 */
export const CLIENT_WAIT_CAP_MS = 12 * 60_000;

export type JobState = 'queued' | 'running' | 'done' | 'failed' | 'cancelled';
/** What the service is busy with, for the progress line; unknown stages read as `detecting`. */
export type JobStage = 'queued' | 'detecting' | 'cleaning' | 'assembling' | 'saving';
/** A floor's own outcome in a job of several plans: the app's summary rows (`#FloorPlanReadyRow` and the rest). */
export type FloorState = 'waiting' | 'running' | 'ready' | 'not_recognized' | 'timed_out' | 'scale_not_matched';
export type FailureCode =
  | 'not_recognized' | 'timed_out' | 'bad_image' | 'too_large' | 'unsupported' | 'too_many'
  | 'busy' | 'network' | 'server' | 'cancelled'
  /** The web's own: the browser refused to store the project. */
  | 'storage';

export interface FloorStatus { index: number; state: FloorState; score?: number }

export interface JobStatus {
  id: string;
  state: JobState;
  stage: JobStage;
  /** Jobs ahead in the queue; 0 — this one is next. */
  position?: number;
  /** 0–1, when the service can tell. */
  progress?: number;
  /** The service's estimate of the whole job, seconds. */
  etaSeconds?: number;
  floors: FloorStatus[];
  result?: { projectUrl: string; score: number };
  error?: { code: FailureCode; message?: string };
  /** How long to wait before asking again, when the service says. */
  retryAfterMs?: number;
}

export class RecognitionError extends Error {
  constructor(readonly code: FailureCode, message: string = code, readonly retryAfterMs?: number) {
    super(message);
    this.name = 'RecognitionError';
  }
}

/** A plan made ready for the service: shrunk to `PLAN_MAX_SIDE`, flattened on white, JPEG. */
export interface PreparedPlan { blob: Blob; name: string; width: number; height: number }

export interface StartRequest {
  floors: PreparedPlan[];
  /** The interface's culture: the service reads room labels and area units with it. */
  culture: string;
}

export interface RecognitionTransport {
  /** The in-browser example, which answers with a ready-made house whatever the pictures show. */
  readonly demo: boolean;
  start(request: StartRequest, onUpload: (fraction: number) => void, signal: AbortSignal): Promise<JobStatus>;
  status(id: string, signal: AbortSignal): Promise<JobStatus>;
  /** The recognized project's XML. */
  project(job: JobStatus, signal: AbortSignal): Promise<string>;
  /** Tells the service to drop the job; nothing waits for the answer. */
  cancel(id: string): void;
}

const JOB_STATES = new Set<JobState>(['queued', 'running', 'done', 'failed', 'cancelled']);
const STAGES = new Set<JobStage>(['queued', 'detecting', 'cleaning', 'assembling', 'saving']);
const FLOOR_STATES = new Set<FloorState>(['waiting', 'running', 'ready', 'not_recognized', 'timed_out', 'scale_not_matched']);
const FAILURES = new Set<FailureCode>(['not_recognized', 'timed_out', 'bad_image', 'too_large', 'unsupported', 'too_many', 'busy', 'network', 'server', 'cancelled']);

const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * The service's answer about a job, checked field by field: anything unknown or malformed is dropped rather than
 * trusted, and an answer without an id or a state is not an answer.
 */
export function parseJobStatus(value: unknown): JobStatus {
  if (!value || typeof value !== 'object') throw new RecognitionError('server', 'Malformed job status');
  const raw = value as Record<string, unknown>;
  const id = typeof raw.id === 'string' ? raw.id : '';
  const state = raw.status ?? raw.state;
  if (!id || typeof state !== 'string' || !JOB_STATES.has(state as JobState)) throw new RecognitionError('server', 'Malformed job status');
  const stage = typeof raw.stage === 'string' && STAGES.has(raw.stage as JobStage) ? raw.stage as JobStage : state === 'queued' ? 'queued' : 'detecting';
  const floors = Array.isArray(raw.floors) ? raw.floors.flatMap((floor, index): FloorStatus[] => {
    if (!floor || typeof floor !== 'object') return [];
    const item = floor as Record<string, unknown>;
    const floorState = typeof item.status === 'string' ? item.status : item.state;
    if (typeof floorState !== 'string' || !FLOOR_STATES.has(floorState as FloorState)) return [];
    return [{ index: finite(item.index) ? Math.max(0, Math.round(item.index)) : index, state: floorState as FloorState,
      ...(finite(item.score) ? { score: clamp(item.score, 0, 100) } : {}) }];
  }) : [];
  const status: JobStatus = { id, state: state as JobState, stage, floors };
  if (finite(raw.position)) status.position = Math.max(0, Math.round(raw.position));
  if (finite(raw.progress)) status.progress = clamp(raw.progress, 0, 1);
  if (finite(raw.etaSeconds) && raw.etaSeconds > 0) status.etaSeconds = raw.etaSeconds;
  const result = raw.result as Record<string, unknown> | undefined;
  if (result && typeof result === 'object' && typeof result.projectUrl === 'string' && result.projectUrl)
    status.result = { projectUrl: result.projectUrl, score: finite(result.score) ? clamp(result.score, 0, 100) : 0 };
  const error = raw.error as Record<string, unknown> | undefined;
  if (error && typeof error === 'object') {
    const code = typeof error.code === 'string' && FAILURES.has(error.code as FailureCode) ? error.code as FailureCode : 'server';
    status.error = { code, ...(typeof error.message === 'string' ? { message: error.message } : {}) };
  }
  if (status.state === 'done' && !status.result) throw new RecognitionError('server', 'A finished job without a project');
  return status;
}

/** A weak result opens on the plan, where its walls are fixed, as the app opens it in wall editing. */
export const opensOnPlan = (score: number): boolean => score < HIGH_QUALITY_SCORE;
/** An almost perfect result does not ask to be checked. */
export const needsCheck = (score: number): boolean => score < CHECK_SCORE;
